<script setup lang="ts">
import { Dialog, Button } from 'primevue';
import { computed, ref, watch } from 'vue';
import { useGetMapStyle } from '../api/getMapStyle';
import type { ProblemDetails } from 'shared/problems/domain/problemDetails';
import ProblemDetailsView from 'shared/problems/components/ProblemDetailsView.vue';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import { useDeleteMapStyle } from '../api/deleteMapStyle';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';

defineExpose({
  show
});

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const dialogTranslations = computed(() => translations.value.mapStyles.deleteDialog);

const styleId = ref<string>();
const styleName = ref<string>('');
const visible = ref(false);
const loading = ref(false);
const getMapStyle = useGetMapStyle();
const problems = ref<ProblemDetails>();
const actionFailedAlert = useActionFailedAlert();

const deleteMapStyle = useDeleteMapStyle();

watch(styleId, (newId) => {
  if (!newId) {
    visible.value = false;
  }
});

async function show(id: string) {
  try {
    problems.value = undefined;
    loading.value = true;
    const response = await getMapStyle.execute({ styleId: id });

    if (response.ok) {
      const style = response.style;
      styleId.value = style.id;
      styleName.value = style.name;
    } else {
      actionFailedAlert.show(response.details);
      return;
    }


    visible.value = true;
  } finally {
    loading.value = false;
  }
}

async function executeDelete() {
  const id = styleId.value;
  if (!id) throw new Error();
  try {
    loading.value = true;
    const response = await deleteMapStyle.execute({ styleId: id});

    if (response.ok) {
      styleId.value = undefined;
      visible.value = false;
    } else {
      problems.value = response.details;
    }

  } finally {
    loading.value = false;
  }
}

function onHide() {
  styleId.value = undefined;
  visible.value = false;
}

</script>

<template>
  <Dialog modal @hide="onHide" :header="dialogTranslations.header(styleName)" v-model:visible="visible">
    <ProblemDetailsView v-model:model-value="problems"></ProblemDetailsView>
    <div class="flex items-center gap-4">
      <i class="pi pi-exclamation-triangle !text-3xl" style="color: var(--p-red-400)"></i>
      <span>{{ dialogTranslations.description }}</span>
    </div>
    <template #footer>
      <Button :label="dialogTranslations.deleteButton" icon="pi pi-check" @click="executeDelete"></Button>
      <Button :label="translations.generic.cancel" icon="pi pi-times" text @click="onHide"></Button>
    </template>
  </Dialog>
</template>
